// WebAuthn travels as JSON with binary fields in base64url; the browser API wants ArrayBuffers.

export const base64UrlToBuffer = (value: string): ArrayBuffer => {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/').replace(/=+$/, '')
  const binary = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4))
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }

  return bytes.buffer
}

export const bufferToBase64Url = (buffer: ArrayBuffer): string => {
  let binary = ''
  new Uint8Array(buffer).forEach((byte) => {
    binary += String.fromCharCode(byte)
  })

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export const isWebAuthnAvailable = (): boolean =>
  typeof window !== 'undefined' &&
  typeof (window as any).PublicKeyCredential !== 'undefined' &&
  !!navigator.credentials

type ServerDescriptor = { type: string; id: string; transports?: string[] }

const toDescriptors = (list?: ServerDescriptor[]): PublicKeyCredentialDescriptor[] =>
  (list ?? []).map((item) => ({
    type: 'public-key',
    id: base64UrlToBuffer(item.id),
    transports: item.transports as AuthenticatorTransport[] | undefined,
  }))

export const toRequestOptions = (json: any): PublicKeyCredentialRequestOptions => ({
  ...json,
  challenge: base64UrlToBuffer(json.challenge),
  allowCredentials: toDescriptors(json.allowCredentials),
})

export const toCreationOptions = (json: any): PublicKeyCredentialCreationOptions => ({
  ...json,
  challenge: base64UrlToBuffer(json.challenge),
  user: { ...json.user, id: base64UrlToBuffer(json.user.id) },
  excludeCredentials: toDescriptors(json.excludeCredentials),
})

/** An assertion or an attestation in the shape the server reads. */
export const credentialToJson = (credential: PublicKeyCredential): Record<string, unknown> => {
  const response = credential.response as any
  const fields: Record<string, unknown> = {
    clientDataJSON: bufferToBase64Url(response.clientDataJSON),
  }

  if (response.attestationObject) {
    fields.attestationObject = bufferToBase64Url(response.attestationObject)
  } else {
    fields.authenticatorData = bufferToBase64Url(response.authenticatorData)
    fields.signature = bufferToBase64Url(response.signature)
    fields.userHandle = response.userHandle ? bufferToBase64Url(response.userHandle) : null
  }

  return {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    response: fields,
  }
}
